import React from "react";
import { ocIsStaleBuildError, ocReloadForNewBuild } from "../../lib/oc-stale-build";

interface OcErrorBoundaryProps {
  children: React.ReactNode;
}

interface OcErrorBoundaryState {
  hasError: boolean;
  /** The error was a stale build (old chunk after a deploy) and a reload is under way. */
  reloading: boolean;
}

/**
 * Top-level error boundary. Catches uncaught render errors anywhere in the
 * tree and shows an on-brand fallback card instead of a blank white screen.
 */
export class OcErrorBoundary extends React.Component<
  OcErrorBoundaryProps,
  OcErrorBoundaryState
> {
  constructor(props: OcErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, reloading: false };
  }

  static getDerivedStateFromError(error: unknown): OcErrorBoundaryState {
    return { hasError: true, reloading: ocIsStaleBuildError(error) };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    // A page whose code was replaced by a newer deploy: reload to the current
    // build instead of showing an error (once — see ocReloadForNewBuild).
    if (ocIsStaleBuildError(error)) {
      if (ocReloadForNewBuild()) return;
      this.setState({ reloading: false });
    }
    console.error("OcErrorBoundary caught an error:", error, info.componentStack);
  }

  render() {
    if (!this.state.hasError) return this.props.children;
    if (this.state.reloading) {
      return (
        <div className="min-h-screen flex items-center justify-center px-4 bg-slate-50 dark:bg-oc-navy-900">
          <p className="text-sm text-slate-500 dark:text-slate-400">Loading the latest version…</p>
        </div>
      );
    }

    return (
      <div className="min-h-screen flex items-center justify-center px-4 bg-slate-50 dark:bg-oc-navy-900">
        <div className="w-full max-w-md rounded-xl border border-slate-200 dark:border-slate-700/50 bg-white dark:bg-oc-navy-800 p-8 text-center shadow-sm">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            strokeWidth={1.5}
            stroke="currentColor"
            className="mx-auto h-12 w-12 text-amber-500 dark:text-amber-400"
            aria-hidden="true"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126ZM12 15.75h.007v.008H12v-.008Z"
            />
          </svg>
          <h1 className="mt-4 text-lg font-semibold text-slate-800 dark:text-slate-100">
            Something went wrong
          </h1>
          <p className="mt-2 text-sm text-slate-500 dark:text-slate-400">
            An unexpected error occurred. Reloading the page usually fixes it.
          </p>
          <button
            type="button"
            onClick={() => location.reload()}
            className="mt-6 inline-flex items-center justify-center rounded-lg bg-oc-teal-500 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-oc-teal-400 focus:outline-none focus:ring-2 focus:ring-oc-teal-400 focus:ring-offset-2 focus:ring-offset-white dark:focus:ring-offset-oc-navy-800"
          >
            Reload
          </button>
        </div>
      </div>
    );
  }
}
